import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRecurring } from "../hooks/useRecurring";
import { useWorkspace } from "../hooks/useWorkspace";
import { usePendientes } from "../hooks/usePendientes";
import { useEntries } from "../hooks/useEntries";
import { useBiometricLock } from "../hooks/useBiometricLock";
import LockScreen from "./LockScreen";
import { t } from "../i18n";
import {
  LayoutDashboard, LineChart, CreditCard, Plus, RefreshCw, Search, Clock,
  Contact, Target, PiggyBank, TrendingUp, Users, Settings, Bell, Star,
  Gift, User, LogOut, Menu, AlertTriangle, ChevronDown,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, profile, logout, effectivePlan, lang } = useAuth();
  const bioLock = useBiometricLock(user?.uid, user?.email);
  const { processRecurrents, recurrents, loading: recLoading } = useRecurring();
  const { workspaces, activeWorkspaceId, isPersonalActive, switchWorkspace } = useWorkspace();
  const { pendientes } = usePendientes();
  const { thisMonthCount, FREE_LIMITS } = useEntries();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen] = useState(false);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const T = (key) => t(lang, key);
  const c = profile?.config || {};
  const budgetLabel = c.budget_label || (lang==="en"?"Budgets":"Presupuestos");
  const activeWorkspace = workspaces.find(w => w.id === activeWorkspaceId);
  const isPro    = effectivePlan === "pro";

  // Avisos automáticos — se calculan al vuelo cada vez, nunca se guardan
  // en ningún sitio: pendientes vencidos o a punto de vencer (a partir de
  // "due", el campo de fecha en Pendientes.js), y aviso si el plan
  // gratuito está cerca de su límite mensual de entradas.
  const DUE_SOON_DAYS = 3;
  const notifications = (() => {
    const list = [];
    const today = new Date(); today.setHours(0,0,0,0);

    (pendientes || []).forEach(p => {
      if (!p.due) return;
      const d = new Date(p.due); d.setHours(0,0,0,0);
      const diffDays = Math.round((d - today) / 86400000);
      const dateStr = new Date(p.due).toLocaleDateString(lang==="en"?"en-GB":"es-ES");
      if (diffDays < 0) {
        list.push({
          id: `venc-${p.id}`, icon: AlertTriangle, urgent: true,
          text: lang==="en" ? `"${p.desc}" was due on ${dateStr}` : `"${p.desc}" venció el ${dateStr}`,
        });
      } else if (diffDays <= DUE_SOON_DAYS) {
        list.push({
          id: `pronto-${p.id}`, icon: Clock, urgent: false,
          text: lang==="en" ? `"${p.desc}" is due on ${dateStr}` : `"${p.desc}" vence el ${dateStr}`,
        });
      }
    });
    // Vencidos primero, luego los que vencen antes
    list.sort((a,b) => (a.urgent===b.urgent) ? 0 : (a.urgent ? -1 : 1));

    if (!isPro && FREE_LIMITS && thisMonthCount >= FREE_LIMITS.entriesPerMonth * 0.8) {
      list.push({
        id: "limite-plan", icon: Gift, urgent: thisMonthCount >= FREE_LIMITS.entriesPerMonth,
        text: lang==="en"
          ? `You've used ${thisMonthCount}/${FREE_LIMITS.entriesPerMonth} free entries this month`
          : `Has usado ${thisMonthCount}/${FREE_LIMITS.entriesPerMonth} entradas gratuitas este mes`,
      });
    }
    return list;
  })();

  const isNegocio = profile?.perfil === "negocio";
  const NAV = [
    { path:"/panel",         icon:LayoutDashboard, label:T("nav_panel") },
    { path:"/graficos",      icon:LineChart,       label:T("nav_charts") },
    { path:"/cuentas",       icon:CreditCard,       label:T("nav_accounts") },
    { path:"/anadir",        icon:Plus,             label:T("nav_add") },
    { path:"/recurrentes",   icon:RefreshCw,        label:lang==="en"?"Recurring":"Recurrentes" },
    { path:"/historial",     icon:Search,           label:T("nav_history") },
    { path:"/pendientes",    icon:Clock,             label:lang==="en"?"Pending":"Pendientes" },
    ...(isNegocio ? [{ path:"/contactos", icon:Contact, label:lang==="en"?"Contacts":"Contactos" }] : []),
    { path:"/presupuestos",  icon:Target,           label:budgetLabel },
    { path:"/huchas",        icon:PiggyBank,        label:lang==="en"?"Savings jars":"Huchas" },
    { path:"/inversiones",   icon:TrendingUp,       label:lang==="en"?"Investments":"Inversiones" },
    { path:"/cuenta-compartida", icon:Users,        label:lang==="en"?"Shared account":"Cuenta compartida" },
    { path:"/ajustes",       icon:Settings,         label:T("nav_settings") },
  ];

  const BOTTOM_NAV = [
    { path:"/panel",     icon:LayoutDashboard, label:T("nav_panel") },
    { path:"/graficos",  icon:LineChart,       label:T("nav_charts") },
    { path:"/anadir",    icon:Plus,            label:T("nav_add"), primary: true },
    { path:"/historial", icon:Search,          label:T("nav_history") },
    { path:"/ajustes",   icon:Settings,        label:T("nav_settings") },
  ];

  const initials = profile?.name?.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2) || "?";

  useEffect(() => {
    if (!recLoading && recurrents.length > 0) {
      processRecurrents();
    }
  }, [recLoading, recurrents.length]);

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  const NotificationBell = ({ align = "right" }) => (
    <div style={{position:"relative"}}>
      <button onClick={()=>setNotifOpen(!notifOpen)}
        style={{position:"relative", background:"rgba(255,255,255,.12)", border:"none",
                borderRadius:"50%", width:34, height:34, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center"}}>
        <Bell size={17} color="#fff" />
        {notifications.length > 0 && (
          <span style={{position:"absolute", top:-2, right:-2, background:"var(--red,#dc2626)",
                        color:"#fff", fontSize:10, fontWeight:700, borderRadius:10,
                        minWidth:16, height:16, display:"flex", alignItems:"center",
                        justifyContent:"center", padding:"0 3px", border:"2px solid var(--gray-900)"}}>
            {notifications.length > 9 ? "9+" : notifications.length}
          </span>
        )}
      </button>
      {notifOpen && (
        <>
          <div style={{position:"fixed", inset:0, zIndex:59}} onClick={()=>setNotifOpen(false)} />
          <div style={{position:"absolute", top:"calc(100% + 8px)", [align]:0, width:300,
                       maxHeight:360, overflowY:"auto", background:"var(--card,#fff)",
                       border:"1px solid var(--border,#e5e7eb)", borderRadius:12,
                       boxShadow:"var(--shadow-md,0 8px 28px rgba(0,0,0,.18))", zIndex:61}}>
            <div style={{padding:"12px 14px", borderBottom:"1px solid var(--border,#e5e7eb)",
                         fontSize:13, fontWeight:700, color:"var(--gray-800,#1f2937)",
                         display:"flex", alignItems:"center", gap:8}}>
              <Bell size={15} /> {lang==="en"?"Notifications":"Notificaciones"}
            </div>
            {notifications.length === 0 ? (
              <p style={{padding:"20px 14px", fontSize:13, color:"var(--gray-400,#9ca3af)", textAlign:"center"}}>
                {lang==="en" ? "You're all caught up." : "No tienes avisos pendientes."}
              </p>
            ) : (
              <div style={{display:"flex", flexDirection:"column"}}>
                {notifications.map(n => (
                  <div key={n.id}
                    onClick={()=>{ setNotifOpen(false); navigate(n.id==="limite-plan" ? "/pro" : "/pendientes"); }}
                    style={{display:"flex", gap:10, padding:"10px 14px", cursor:"pointer",
                            borderBottom:"1px solid var(--border,#e5e7eb)", fontSize:13}}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--gray-50,#f9fafb)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <n.icon size={16} style={{flexShrink:0, marginTop:1,
                                              color: n.urgent ? "var(--red,#dc2626)" : "var(--gray-400,#9ca3af)"}} />
                    <span style={{fontWeight:n.urgent?600:400, color:"var(--gray-800,#1f2937)"}}>{n.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const Sidebar = () => (
    <aside className={`sidebar ${open?"open":""}`}>
      <div className="sidebar-logo" style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8}}>
        <div style={{display:"flex", alignItems:"center", gap:10, minWidth:0}}>
          <img src="/icon-512.png" alt="" style={{width:30, height:30, borderRadius:8, flexShrink:0}} />
          <div style={{minWidth:0}}>
            <h1 style={{margin:0}}>{T("app_name")}</h1>
            <span>by AriaPC</span>
          </div>
        </div>
        <div className="sidebar-bell-only-desktop">
          <NotificationBell align="left" />
        </div>
      </div>

      {workspaces.length > 1 && (
        <div style={{padding:"0 16px 12px",position:"relative"}}>
          <button onClick={()=>setWsMenuOpen(!wsMenuOpen)}
            style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,
                    padding:"8px 12px",borderRadius:8,border:"1px solid var(--border)",
                    background:"var(--gray-50)",cursor:"pointer",fontSize:13}}>
            <span style={{display:"flex",alignItems:"center",gap:6,overflow:"hidden"}}>
              {isPersonalActive ? <User size={14}/> : <Users size={14}/>}
              <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:"var(--gray-700)",fontWeight:600}}>
                {activeWorkspace?.name || (lang==="en"?"Personal":"Personal")}
              </span>
            </span>
            <ChevronDown size={14} color="var(--gray-400)" />
          </button>
          {wsMenuOpen && (
            <div style={{position:"absolute",top:"100%",left:16,right:16,marginTop:4,
                         background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,
                         boxShadow:"var(--shadow-md,0 6px 20px rgba(0,0,0,.15))",zIndex:50,overflow:"hidden"}}>
              {workspaces.map(ws => (
                <button key={ws.id}
                  onClick={()=>{ switchWorkspace(ws.id); setWsMenuOpen(false); }}
                  style={{width:"100%",textAlign:"left",padding:"10px 12px",border:"none",cursor:"pointer",
                          background: ws.id===activeWorkspaceId ? "var(--blue-l)" : "transparent",
                          color: ws.id===activeWorkspaceId ? "var(--blue)" : "var(--gray-700)",
                          fontSize:13,fontWeight: ws.id===activeWorkspaceId ? 600 : 400,
                          display:"flex",alignItems:"center",gap:8}}>
                  {ws.isPersonal ? <User size={14}/> : <Users size={14}/>}
                  {ws.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <nav className="sidebar-nav">
        {NAV.map(n => (
          <button key={n.path}
            className={`nav-item ${location.pathname===n.path?"active":""}`}
            onClick={()=>{ navigate(n.path); setOpen(false); }}>
            <span className="icon"><n.icon size={18}/></span>
            {n.label}
          </button>
        ))}
      </nav>
      <div className="sidebar-bottom">
        <div className="plan-badge">
          {isPro && <strong style={{display:"flex",alignItems:"center",gap:6}}><Star size={14}/> {T("nav_pro")}</strong>}
          {!isPro && <strong style={{display:"flex",alignItems:"center",gap:6}}><Gift size={14}/> {T("nav_free")}</strong>}
          {!isPro && <span>{T("nav_free_limit")}</span>}
          {isPro && <span>{T("nav_full_access")}</span>}
        </div>
        {!isPro && (
          <button className="nav-item" style={{color:"#a78bfa",marginBottom:4}}
            onClick={()=>{ navigate("/pro"); setOpen(false); }}>
            <span className="icon"><Star size={18}/></span>{T("nav_go_pro")}
          </button>
        )}
        <button className="nav-item" onClick={()=>{ navigate("/perfil"); setOpen(false); }}>
          <span className="icon"><User size={18}/></span>
          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {profile?.name || user?.email}
          </span>
        </button>
        <button className="nav-item" onClick={handleLogout}>
          <span className="icon"><LogOut size={18}/></span>{T("nav_logout")}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="app-shell">
      {bioLock.locked && (
        <LockScreen
          lang={lang}
          verifying={bioLock.verifying}
          lastError={bioLock.lastError}
          onUnlock={bioLock.verify}
          onLogout={()=>{ logout().then(()=>navigate("/")); }}
        />
      )}
      <Sidebar />
      {open && <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:99}} onClick={()=>setOpen(false)} />}

      <div className="main-content" style={{paddingBottom:0}}>
        <div className="mobile-header">
          <button className="hamburger" onClick={()=>setOpen(!open)}><Menu size={22}/></button>
          <span style={{fontSize:14,fontWeight:600}}>{T("app_name")}</span>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <NotificationBell />
            <div style={{width:32,height:32,borderRadius:"50%",background:"var(--blue,#4f46e5)",
                         display:"flex",alignItems:"center",justifyContent:"center",
                         fontSize:12,fontWeight:700,color:"white"}}>
              {initials}
            </div>
          </div>
        </div>

        <div style={{paddingBottom:"env(safe-area-inset-bottom)"}}>
          {children}
        </div>

        <div className="desktop-footer" style={{borderTop:"1px solid var(--border)",padding:"16px 32px",
                     display:"flex",alignItems:"center",justifyContent:"space-between",
                     flexWrap:"wrap",gap:8}}>
          <span style={{fontSize:12,color:"var(--gray-400)"}}>© 2026 AriaPC · ARIA Wallet</span>
          <div style={{display:"flex",gap:16}}>
            {[
              [lang==="en"?"Privacy":"Privacidad","privacidad"],
              [lang==="en"?"Terms":"Términos","terminos"],
              [lang==="en"?"Legal Notice":"Aviso Legal","aviso"],
              ["Cookies","cookies"]
            ].map(([label, section])=>(
              <a key={section} href={`/legal?tab=${section}`}
                 style={{fontSize:12,color:"var(--gray-400)",textDecoration:"none"}}
                 onMouseEnter={e=>e.target.style.color="var(--blue)"}
                 onMouseLeave={e=>e.target.style.color="var(--gray-400)"}>
                {label}
              </a>
            ))}
          </div>
        </div>

        <nav className="bottom-nav">
          {BOTTOM_NAV.map(n => (
            <button key={n.path}
              onClick={()=>navigate(n.path)}
              style={{
                flex: 1, display:"flex", flexDirection:"column", alignItems:"center",
                justifyContent:"center", gap:2, padding:"8px 4px",
                background:"none", border:"none", cursor:"pointer",
                color: location.pathname===n.path ? "var(--blue,#4f46e5)" : "var(--gray-400)",
                position:"relative",
              }}>
              {n.primary ? (
                <div style={{
                  width:48, height:48, borderRadius:"50%",
                  background:"var(--blue,#4f46e5)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  marginTop:-20,
                  boxShadow:"0 4px 12px rgba(79,70,229,.4)"
                }}>
                  <Plus size={24} color="#fff" />
                </div>
              ) : (
                <n.icon size={20} />
              )}
              <span style={{fontSize:10,fontWeight:location.pathname===n.path?700:400}}>
                {n.label}
              </span>
              {location.pathname===n.path && !n.primary && (
                <div style={{position:"absolute",bottom:0,width:20,height:3,
                             borderRadius:2,background:"var(--blue,#4f46e5)"}} />
              )}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
